import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { MinutesPerHour } from 'rev-shared/date/Time.Constant';
import { FeatureName } from 'rev-shared/security/FeatureName';
import { UserAccountService } from 'rev-shared/security/UserAccount.Service';

export interface IVideoSummary {
	avgViewingDurationSecs: number;
	completionRate: number;
	totalVideoViews: number;
	uniqueViewers: number;
	viewsByDay: any[];
}

export interface IUsersSummary {
	distinctLogins: number;
	licenseSummary: any;
	logonsByBrowser: [{key: string; value: number}];
	logonsByDay: [{key: string; value: number}];
	logonsByDevice: [{key: string; value: number}];
}


@Injectable()
export class AdminReportsService {

	constructor(
		private http: HttpClient,
		private DateParsers: DateParsersService,
		private UserAccount: UserAccountService
	) {}

	public getVideoTopHits(accountId: string, topCount: number, params: any): Observable<any> {
		return this.http.get<any>(`/reports/account/${accountId}/videos/top-hits/${topCount}`, { params });
	}

	public getVideosActivitySummary(accountId: string, params: any): Observable<IVideoSummary> {
		return this.http.get<IVideoSummary>(`/reports/account/summary/${accountId}/videos`, { params });
	}

	public getUsageActivitySummary(accountId: string): Observable<any> {
		const isConcurrentUsersLicense = this.UserAccount.currentAdminAccount.isConcurrentUsersLicense();
		return forkJoin([
			this.http.get<any>(`/reports/account/summary/${accountId}/usage`),
			isConcurrentUsersLicense ? this.http.get<any>(`/reports/account/summary/${accountId}/concurrent-users`) : of(null)
		])
			.pipe(
				map(responses => {
					const usageSummary = responses[0];
					usageSummary.viewingHoursBuckets.forEach(bucket => {
						this.shapeUsageHoursBucket(bucket);
					});

					const aiCreditsLicense = usageSummary.aiCreditsLicense;
					aiCreditsLicense.buckets.forEach(bucket => {
						this.shapeUsageHoursBucket(bucket, false, aiCreditsLicense.feature);
					});
					usageSummary.activeAICreditsBuckets = aiCreditsLicense.buckets.filter(bucket => bucket.expiresOn > Date.now());

					[...usageSummary.distributionHoursBuckets].forEach(bucket => {
						this.shapeUsageHoursBucket(bucket, true);
					});

					usageSummary.viewingHours = {
						...usageSummary.viewingHours,
						...{
							summary: this.parseTimespan(usageSummary.viewingHours.summary),
							vod: this.parseTimespan(usageSummary.viewingHours.vod),
							webcast: this.parseTimespan(usageSummary.viewingHours.webcast),
							vc: this.parseTimespan(usageSummary.viewingHours.vc)
						}
					};

					usageSummary.activeUsersLicense = this.shapeUsersLicense(usageSummary.activeUsersLicense);

					return {
						usageSummary,
						concurrentUsersUsage: this.shapeUsersLicense(responses[1])
					};
				})
			);
	}

	private shapeUsersLicense(licenseUsageDetails): any {
		if (licenseUsageDetails) {
			const firstNonZeroActiveUsersMonth = licenseUsageDetails.buckets.findIndex(element => element.activeUsers > 0);
			licenseUsageDetails.buckets.splice(0, firstNonZeroActiveUsersMonth);

			licenseUsageDetails.buckets.forEach(bucket => {
				bucket.key = new Date(bucket.year, bucket.month - 1, 1);
			});
		}
		return licenseUsageDetails;
	}

	public shapeUsageHoursBucket(bucket: any, isOverageBarDisplayed?: boolean, feature: string = null): void {
		//value is as is if feature for the bucket is in credits (db representation is microcredits)
		const isAiCredits = feature === FeatureName.AI_MICROCREDITS;
		const unitConversion = isAiCredits ? 1 : MinutesPerHour;

		bucket.allocatedHours = Math.round(bucket.allocated / unitConversion);
		bucket.usedHours = isOverageBarDisplayed ?
			Math.round(bucket.used / unitConversion) : // for buckets showing overage bar
			Math.min(bucket.allocatedHours, Math.round(bucket.used / unitConversion));
		bucket.availableHours = bucket.allocatedHours - bucket.usedHours;
		bucket.overageHours = Math.max(0, bucket.usedHours - bucket.allocatedHours);
		bucket.overagePercentage = bucket.overageHours ? (bucket.overageHours / bucket.usedHours) * 100 : 0;
		bucket.usedPercentage = (bucket.usedHours / bucket.allocatedHours) * 100;
		bucket.expiresOn = this.DateParsers.parseUTCDate(bucket.expiresOn);
	}

	public getUserActivitySummary(accountId: string): Observable<IUsersSummary> {
		return this.http.get<IUsersSummary>(`/reports/user-activity/summary/${accountId}`)
			.pipe(
				shareReplay({ bufferSize: 1, refCount: true })
			);
	}

	private parseTimespan(obj: any): any {
		Object.keys(obj).forEach(
			key => obj[key] = this.DateParsers.parseTimespan(obj[key])
		);

		return obj;
	}
}
